import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';
import { useWebSocket } from '../WebSocketContext';

const ActionsFormExtraction = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [sourceMode, setSourceMode] = useState('current');
  const [imagePath, setImagePath] = useState('');
  const [outputTag, setOutputTag] = useState('form_extraction');
  const [filePathError, setFilePathError] = useState('');
  const [tagError, setTagError] = useState('');
  const [stage1VisionModelId, setStage1VisionModelId] = useState('');
  const [stage2VisionModelId, setStage2VisionModelId] = useState('');
  const [savedVisionModels, setSavedVisionModels] = useState([]);
  const [browseDisabled, setBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const { ws, sendMessage, subscribeToMessages } = useWebSocket();

  const imagePathRef = useRef(null);
  const outputTagRef = useRef(null);
  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const allowedRootsText = restrictToUserFolder
    ? (allowedRoots?.length ? allowedRoots.join(', ') : (displayRoot || t('form_extraction.file_path_root_label', 'your restricted root')))
    : '';
  const filePathHelperText = filePathError
    ? filePathError
    : (restrictToUserFolder
        ? t('form_extraction.file_path_help', 'Paths must stay within allowed folders: {allowed}', { allowed: allowedRootsText })
        : t('form_extraction.file_path_help_any', 'Paths must be accessible to the server.')
      );

  const { handleBrowseDirectory: openFileBrowser, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setImagePath(selectedPath);
        if (filePathError) {
          setFilePathError('');
        }
      }
      setBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowseDisabled(false);
    }
  });

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    id,
    settingsLoaded,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'formextraction',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return false;
      return (
        String(initialData.sourceMode || 'current') === sourceMode &&
        String(initialData.imagePath || '') === imagePath &&
        String(initialData.tag || 'form_extraction') === outputTag &&
        String(initialData.stage1VisionModelId || '') === stage1VisionModelId &&
        String(initialData.stage2VisionModelId || '') === stage2VisionModelId
      );
    },
    customSaveData: () => ({
      sourceMode,
      imagePath: sourceMode === 'other' ? imagePath.trim() : '',
      tag: outputTag.trim(),
      stage1VisionModelId: stage1VisionModelId || '',
      stage2VisionModelId: stage2VisionModelId || '',
      useConditions: true,
      conditions
    }),
    customForceRunData: () => ({
      sourceMode,
      imagePath: sourceMode === 'other' ? imagePath.trim() : '',
      tag: outputTag.trim(),
      stage1VisionModelId: stage1VisionModelId || '',
      stage2VisionModelId: stage2VisionModelId || '',
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse form extraction data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(parsedData.sourceMode || 'current');
      setImagePath(parsedData.imagePath || '');
      setOutputTag(parsedData.tag || 'form_extraction');
      setStage1VisionModelId(
        parsedData.stage1VisionModelId !== undefined
          ? String(parsedData.stage1VisionModelId || '')
          : String(parsedData.stage1_vision_model_id || '')
      );
      setStage2VisionModelId(
        parsedData.stage2VisionModelId !== undefined
          ? String(parsedData.stage2VisionModelId || '')
          : String(parsedData.stage2_vision_model_id || '')
      );
    }
  });

  useEffect(() => {
    updateSaveDisabledState?.();
  }, [sourceMode, imagePath, outputTag, stage1VisionModelId, stage2VisionModelId, updateSaveDisabledState]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (message?.command !== 'saved_models') {
        return;
      }
      const visionModels = Array.isArray(message.data?.vision) ? message.data.vision : [];
      setSavedVisionModels(visionModels);
    });

    if (ws?.readyState === WebSocket.OPEN) {
      sendMessage({
        command: 'get_saved_models',
        data: {}
      });
    }

    return () => unsubscribe();
  }, [sendMessage, subscribeToMessages, ws]);

  const handleBrowse = useCallback(() => {
    setBrowseDisabled(true);
    openFileBrowser();
  }, [openFileBrowser]);

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'imagePath') {
      imagePathRef.current?.insertTag(value);
    } else if (menuType === 'outputTag') {
      outputTagRef.current?.insertTag(value);
    }
    handleMenuClose();
  }, [menuType, handleMenuClose]);

  const handleCustomSave = useCallback(() => {
    const trimmedImagePath = imagePath.trim();
    const trimmedTag = outputTag.trim();

    let hasError = false;

    if (sourceMode === 'other' && !trimmedImagePath) {
      setFilePathError(t('form_extraction.file_path_error_required', 'An image or PDF path is required before saving.'));
      hasError = true;
    } else if (filePathError) {
      setFilePathError('');
    }

    if (!trimmedTag) {
      setTagError(t('form_extraction.output_tag_error_required', 'An output tag is required before saving.'));
      hasError = true;
    } else if (tagError) {
      setTagError('');
    }

    if (!hasError) {
      handleSave();
    }
  }, [filePathError, handleSave, imagePath, outputTag, sourceMode, t, tagError]);

  const formatVisionModelLabel = useCallback((model) => {
    if (!model || typeof model !== 'object') {
      return t('form_extraction.model_row_default_label', 'Vision model');
    }

    const provider = String(model.provider || '').trim();
    const modelName = String(model.model_name || '').trim();
    const labelParts = [provider, modelName].filter(Boolean);
    const baseLabel = labelParts.length > 0
      ? labelParts.join(' • ')
      : t('form_extraction.model_row_default_label', 'Vision model');
    const idSuffix = model.id !== undefined && model.id !== null ? ` (#${model.id})` : '';
    return `${baseLabel}${idSuffix}`;
  }, [t]);

  return (
    <ActionSettingsLayout
      title={t('workflow.formextraction', 'Form Extraction')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      allowForceRunWithUnsavedChanges={settingsLoaded && !(typeof id === 'string' && id.startsWith('temp_'))}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '8px', border: '1px solid #7b61ff10', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' } }}>{t('form_extraction.file_path_label', 'Source Image or PDF Path')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={sourceMode}
                  onChange={(e) => {
                    setSourceMode(e.target.value);
                    if (filePathError) setFilePathError('');
                  }}
                >
                  <FormControlLabel
                    value="current"
                    control={<Radio size="small" sx={{ color: '#7b61ff', '&.Mui-checked': { color: '#7b61ff' } }} />}
                    label={t('form_extraction.source_mode_current', 'Current Source')}
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio size="small" sx={{ color: '#7b61ff', '&.Mui-checked': { color: '#7b61ff' } }} />}
                    label={t('form_extraction.source_mode_other', 'Other Path')}
                  />
                </RadioGroup>
              </FormControl>
              {sourceMode === 'current' && (
                <Typography variant="body2" color="text.secondary">
                  {t('form_extraction.source_mode_current_help', 'The source file of the current document will be used automatically.')}
                </Typography>
              )}
              {sourceMode === 'other' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Box sx={{ flex: 1 }}>
                      <TagEditor
                        caseid={caseid}
                        ref={imagePathRef}
                        value={imagePath}
                        onChange={(value) => {
                          setImagePath(value);
                          if (filePathError) {
                            setFilePathError('');
                          }
                        }}
                        placeholder={t('form_extraction.file_path_placeholder', 'Enter the image or PDF path (tags allowed)')}
                        singleLine
                        tags={tags}
                        onTagClick={(e) => handleMenuOpen(e, 'imagePath')}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<FolderOpenIcon />}
                      onClick={handleBrowse}
                      disabled={browseDisabled}
                      sx={{
                        borderRadius: '0 8px 8px 0',
                        borderColor: '#7b61ff',
                        color: '#7b61ff',
                        textTransform: 'none',
                        ml: -0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(123, 97, 255, 0.08)',
                          borderColor: '#7b61ff',
                        }
                      }}
                    >
                      {t('common.browse', 'Browse')}
                    </Button>
                  </Box>
                  <Typography variant="caption" color={filePathError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                    {filePathHelperText}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '8px', border: '1px solid #7b61ff10', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' } }}>{t('form_extraction.output_tag_label', 'Output Tag')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <TagEditor
                caseid={caseid}
                ref={outputTagRef}
                value={outputTag}
                onChange={(value) => {
                  setOutputTag(value);
                  if (tagError) {
                    setTagError('');
                  }
                }}
                placeholder={t('form_extraction.output_tag_placeholder', 'Enter output tag name')}
                singleLine
                tags={tags}
                onTagClick={(e) => handleMenuOpen(e, 'outputTag')}
              />
              <Typography variant="caption" color={tagError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                {tagError || t('form_extraction.output_tag_help', 'Tag where the final JSON array will be saved.')}
              </Typography>

              <Accordion
                defaultExpanded={false}
                disableGutters
                sx={{
                  mt: 3,
                  borderRadius: '8px',
                  border: '1px solid #7b61ff10',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 48,
                    backgroundColor: '#fafbff',
                    '& .MuiAccordionSummary-content': {
                      my: 1,
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#444' }}>
                    {t('form_extraction.configurations_title', 'Configurations')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label={t('form_extraction.stage1_model_label', 'Stage 1 vision model')}
                        value={stage1VisionModelId}
                        onChange={(event) => setStage1VisionModelId(event.target.value)}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">
                          {t('form_extraction.model_default_option', 'Use active vision assignment')}
                        </MenuItem>
                        {savedVisionModels.length > 0 ? (
                          savedVisionModels.map((model) => (
                            <MenuItem key={`stage1-${model.id}`} value={String(model.id)}>
                              {formatVisionModelLabel(model)}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="__no_models__">
                            {t('form_extraction.no_vision_models', 'No vision models configured yet.')}
                          </MenuItem>
                        )}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label={t('form_extraction.stage2_model_label', 'Stage 2 vision model')}
                        value={stage2VisionModelId}
                        onChange={(event) => setStage2VisionModelId(event.target.value)}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">
                          {t('form_extraction.model_default_option', 'Use active vision assignment')}
                        </MenuItem>
                        {savedVisionModels.length > 0 ? (
                          savedVisionModels.map((model) => (
                            <MenuItem key={`stage2-${model.id}`} value={String(model.id)}>
                              {formatVisionModelLabel(model)}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="__no_models__">
                            {t('form_extraction.no_vision_models', 'No vision models configured yet.')}
                          </MenuItem>
                        )}
                      </TextField>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      <TagsDropdown
        tags={tags}
        anchorEl={anchorEl}
        handleTagSelect={handleTagSelect}
        setAnchorEl={handleMenuClose}
        menuType={menuType}
      />
      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsFormExtraction);
